import {
  freeMinutesFromUsedCredits,
  hasStartCreditsLeft,
  useChat,
} from "@/features/chat"
import ClockMicro from "@/assets/img/advisors/clock_micro.svg"
import Dot from "@/assets/svg/advisors/Dot.svg?react"

import type { Advisor } from "../types"
import { RatingStars } from "./rating-stars"

interface AdvisorCardProps {
  advisor: Advisor
  onClick?: () => void
  onButtonClick?: () => void
  buttonTitle?: string
}

/** Tall advisor card (photo, online badge, rating, chat CTA). Ported from AdvisorCardV4. */
export function AdvisorCard({ advisor, onClick, onButtonClick, buttonTitle }: AdvisorCardProps) {
  const { name, photo, exp, reviews, rating } = advisor
  const { hasPurchasedCredits, userUsedCredits } = useChat()
  const freeMinutesLeft =
    userUsedCredits !== null ? freeMinutesFromUsedCredits(userUsedCredits) : null
  // Wait for socket used_credits; hide after start pack is spent or any top-up.
  const showFreeMinutesBadge =
    hasStartCreditsLeft(userUsedCredits) &&
    !hasPurchasedCredits &&
    !!freeMinutesLeft

  return (
    <div
      onClick={onClick}
      className="relative flex h-full min-h-[344px] w-[240px] cursor-pointer select-none flex-col justify-end overflow-hidden rounded-[20px] p-4"
    >
      <img src={photo} alt={name} className="absolute inset-0 size-full object-cover object-top" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-card-purple" />

      <div className="absolute right-2 top-2 flex items-center gap-1 rounded-xl bg-dark-base/80 px-2.5 py-1.5 font-body text-xs text-green [&_svg]:size-2">
        <Dot /> Online
      </div>

      <div className="relative flex flex-col gap-0.5">
        {showFreeMinutesBadge ? (
          <div className="mb-0.5 flex h-[18px] w-fit items-center justify-center gap-1 rounded-full bg-green py-1 pl-1 pr-2">
            <img src={ClockMicro} alt="" className="size-3" />
            <span className="font-body text-[8px] font-bold text-dark-base">
              {freeMinutesLeft} FREE MINUTE{freeMinutesLeft === 1 ? "" : "S"}
            </span>
          </div>
        ) : null}

        <p className="font-display text-xl font-medium text-white">{name}</p>
        <p className="font-body text-sm text-purple-light/70">{exp}</p>
        <div className="mb-[15px] mt-1 flex items-center gap-1">
          <RatingStars rating={rating} className="h-4 w-20 text-gold" />
          <span className="font-body text-sm font-medium text-gold">{rating}</span>
          <span className="font-body text-sm text-purple-light/70">{reviews}</span>
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onButtonClick?.()
          }}
          className="h-11 w-full rounded-[80px] bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white"
        >
          {buttonTitle ?? "Chat"}
        </button>
      </div>
    </div>
  )
}
